import React, { useEffect } from "react";
import {
  BriefcaseIcon,
  GlobeAltIcon,
  ChartBarIcon,
  LightningBoltIcon,
  ShieldCheckIcon,
  CogIcon,
  UserGroupIcon,
  TruckIcon,
  OfficeBuildingIcon,
  CashIcon,
  TrendingUpIcon,
  StarIcon,
  CheckCircleIcon,
  BadgeCheckIcon,
  CollectionIcon,
  FireIcon
} from "@heroicons/react/outline";
import { motion } from "framer-motion";
import GTranslate from "../GTanslate";

// Card Component
const AwardCard = ({ award, index, groupIndex }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.6, delay: index * 0.1 }}
    whileHover={{ 
      y: -12,
      scale: 1.02,
      transition: { duration: 0.3 }
    }}
    className="h-full"
  >
    <div className="h-full bg-white rounded-2xl shadow-xl hover:shadow-2xl overflow-hidden border border-gray-200 transition-all duration-500">
      {/* Card Header with Ribbon */}
      <div className="relative">
        <div className={`h-3 ${groupIndex === 0 ? 'bg-gradient-to-r from-red-700 to-red-600' : 
                         groupIndex === 1 ? 'bg-gradient-to-r from-gray-800 to-gray-700' : 
                         'bg-gradient-to-r from-red-600 to-red-500'}`} />
        
        {/* Award Badge */}
        <div className="absolute -top-4 right-6">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
            <BadgeCheckIcon className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>

      <div className="p-8">
        {/* Company Name */}
        <div className="mb-4">
          <span className={`px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full ${
            groupIndex === 0 ? 'bg-red-50 text-red-700' : 
            groupIndex === 1 ? 'bg-gray-50 text-gray-700' : 
            'bg-red-50 text-red-700'
          }`}>
            {award.company}
          </span>
        </div>

        {/* Award Icon and Title */}
        <div className="flex items-start gap-4 mb-6">
          <div className={`w-16 h-16 rounded-xl flex items-center justify-center shadow-lg ${
            groupIndex === 0 ? 'bg-gradient-to-br from-red-50 to-red-100 border border-red-200' :
            groupIndex === 1 ? 'bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200' :
            'bg-gradient-to-br from-red-50 to-red-100 border border-red-200'
          }`}>
            <award.Icon className={`w-8 h-8 ${
              groupIndex === 0 ? 'text-red-600' :
              groupIndex === 1 ? 'text-gray-700' :
              'text-red-600'
            }`} />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 leading-tight">
              {award.title}
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              {award.category}
            </p>
          </div>
        </div>

        {/* Description */}
        <div className="mb-8">
          <p className="text-gray-600 leading-relaxed">
            {award.description}
          </p>
        </div>

        {/* Performance Metrics */}
        
        {/* Year Badge */}
        <div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-gray-900">Onaylandı</span>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
            groupIndex === 0 ? 'bg-red-100 text-red-700' :
            groupIndex === 1 ? 'bg-gray-100 text-gray-700' :
            'bg-red-100 text-red-700'
          }`}>
            {award.year}
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

// Main Component
const Awards = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const awardGroups = [
    {
      year: "2012",
      title: "EBSO Ödülleri 2012",
      description: "Grup şirketlerinden Fibrosan A.Ş. ve Fiberplast Ltd. Şti. 2011 yılı performansıyla Ege Bölgesi Sanayi Odası tarafından 5 dalda ödüllendirilmiştir.",
      cards: [
        { 
          company: "Fibrosan A.Ş", 
          title: "Kompozit Grubunda En Yüksek İhracat", 
          description: "Kompozit sektöründe yılın en yüksek ihracat rakamına ulaşarak ödül aldı.", 
          category: "İhracat Performansı",
          ranking: "1.",
          rating: 5,
          year: "2011",
          Icon: GlobeAltIcon 
        },
        { 
          company: "Fibrosan A.Ş", 
          title: "Kompozit Grubunda En Yüksek Üretim", 
          description: "Üretim kapasitesi ve verimlilikte sektörün en iyi performansı.", 
          category: "Üretim Performansı",
          ranking: "2.",
          rating: 4,
          year: "2011",
          Icon: CogIcon 
        },
        { 
          company: "Fibrosan A.Ş", 
          title: "Kompozit Grubunda En Yüksek İstihdam", 
          description: "İstihdam artışı ve insan kaynakları yönetiminde başarı.", 
          category: "İnsan Kaynakları",
          ranking: "2.",
          rating: 4,
          year: "2011",
          Icon: UserGroupIcon 
        },
        { 
          company: "Fibrosan A.Ş", 
          title: "En Yüksek Yurt Dışı Satış", 
          description: "Global pazarlardaki satış performansı ile öne çıktı.", 
          category: "Satış Performansı",
          ranking: "3.",
          rating: 3,
          year: "2011",
          Icon: CashIcon 
        },
        { 
          company: "Fiberplast Ltd. Şti.", 
          title: "Yurt Dışı Satış Başarısı", 
          description: "Özel proje bazlı yurt dışı satışlarda üstün performans.", 
          category: "Proje Satışları",
          ranking: "Ödüllü",
          rating: 4,
          year: "2011",
          Icon: TrendingUpIcon 
        }
      ],
    },
    {
      year: "2013",
      title: "EBSO Ödülleri 2013",
      description: "Grup şirketlerinden Fibrosan A.Ş., HLM International ve Fiberplast Ltd. Şti. 2012 yılı performansıyla Ege Bölgesi Sanayi Odası tarafından 6 dalda ödüllendirilmiştir.",
      cards: [
        { 
          company: "Fibrosan - Panolux", 
          title: "İnovasyon Ödülü", 
          description: "Panolux ürün hattı ile yenilikçi ürün geliştirmede başarı.", 
          category: "İnovasyon",
          ranking: "Ödüllü",
          rating: 5,
          year: "2012",
          Icon: LightningBoltIcon 
        },
        { 
          company: "Fibrosan A.Ş", 
          title: "Kompozit Grubunda En Yüksek Üretim", 
          description: "Üretim verimliliği ve kapasite kullanımında üstün performans.", 
          category: "Üretim Performansı",
          ranking: "2.",
          rating: 4,
          year: "2012",
          Icon: CogIcon 
        },
        { 
          company: "Fibrosan A.Ş", 
          title: "En Yüksek İstihdam Artışı", 
          description: "İstihdam politikaları ve çalışan memnuniyetinde liderlik.", 
          category: "İnsan Kaynakları",
          ranking: "2.",
          rating: 4,
          year: "2012",
          Icon: UserGroupIcon 
        },
        { 
          company: "HLM International", 
          title: "Kompozit Grubunda En Yüksek İhracat", 
          description: "İhracat pazarlarındaki genişleme ve satış artışı.", 
          category: "İhracat Performansı",
          ranking: "2.",
          rating: 4,
          year: "2012",
          Icon: GlobeAltIcon 
        },
        { 
          company: "Fiberplast Ltd. Şti.", 
          title: "Üstün İhracat Performansı", 
          description: "Özel proje bazlı ihracatta kayda değer başarı.", 
          category: "Proje İhracatı",
          ranking: "3.",
          rating: 3,
          year: "2012",
          Icon: ChartBarIcon 
        },
        { 
          company: "Fibrosan A.Ş", 
          title: "Tüm KOBİ'ler Arasında En Yüksek İhracat", 
          description: "Ege Bölgesi'ndeki tüm KOBİ'ler arasında ihracatta ilk 5'te yer aldı.", 
          category: "Genel İhracat",
          ranking: "5.",
          rating: 5,
          year: "2012",
          Icon: TrendingUpIcon 
        }
      ],
    },
    {
      year: "2014",
      title: "EBSO Ödülleri 2014",
      description: "Grup şirketlerinden Fibrosan A.Ş. ve HLM International Ltd. Şti. 2013 yılı performansıyla Ege Bölgesi Sanayi Odası tarafından 6 dalda ödüllendirilmiştir.",
      cards: [
        { 
          company: "Fibrosan A.Ş", 
          title: "En Yüksek Yatırım", 
          description: "Yeni tesis ve teknoloji yatırımları ile sektöre öncülük.", 
          category: "Yatırım",
          ranking: "1.",
          rating: 5,
          year: "2013",
          Icon: BriefcaseIcon 
        },
        { 
          company: "Fibrosan A.Ş", 
          title: "Üstün İhracat Performansı", 
          description: "Kompozit sektöründe ihracat hacmi ve pazar çeşitliliği.", 
          category: "İhracat",
          ranking: "2.",
          rating: 4,
          year: "2013",
          Icon: GlobeAltIcon 
        },
        { 
          company: "Fibrosan A.Ş", 
          title: "En Yüksek İstihdam Kalitesi", 
          description: "İstihdam politikaları ve çalışan memnuniyetinde üstün başarı.", 
          category: "İnsan Kaynakları",
          ranking: "2.",
          rating: 4,
          year: "2013",
          Icon: ShieldCheckIcon 
        },
        { 
          company: "HLM International", 
          title: "Taşıt Sanayi Grubunda En Yüksek Üretim", 
          description: "Taşıt sanayi grubunda üretim kapasitesi ve verimlilik.", 
          category: "Üretim",
          ranking: "2.",
          rating: 4,
          year: "2013",
          Icon: CogIcon 
        },
        { 
          company: "HLM International", 
          title: "Taşıt Sanayi Grubunda En Yüksek İhracat", 
          description: "Otomotiv sektörüne yönelik ihracatta öne çıkan performans.", 
          category: "İhracat",
          ranking: "2.",
          rating: 4,
          year: "2013",
          Icon: CashIcon 
        },
        { 
          company: "Fiberplast Ltd. Şti.", 
          title: "Kompozit Grubunda İhracat Başarısı", 
          description: "Özel proje ihracatında sürdürülebilir başarı.", 
          category: "Proje İhracatı",
          ranking: "3.",
          rating: 3,
          year: "2013",
          Icon: TruckIcon 
        }
      ],
    },
  ];

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50 font-montserrat">
      {/* Fixed Language Selector */}
      <div className="fixed top-6 right-6 z-50">

          <GTranslate />
      </div>

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-16 h-1 bg-gradient-to-r from-red-600 to-red-700 rounded-full" />
              <span className="text-sm font-semibold text-red-700 uppercase tracking-wider">Başarılarımız</span>
              <div className="w-16 h-1 bg-gradient-to-r from-red-600 to-red-700 rounded-full" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Ödüller ve <span className="text-red-700">Başarılar</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Yıllar içinde elde ettiğimiz başarılar ve sanayi odalarından aldığımız ödüller
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-16">
            {[
              { value: '17', label: 'Toplam Ödül', icon: BadgeCheckIcon, color: 'bg-red-600' },
              { value: '3', label: 'Yıl Üst Üste', icon: CollectionIcon, color: 'bg-gray-700' },
              { value: '6', label: 'Kategoride Başarı', icon: FireIcon, color: 'bg-red-700' },
            ].map((stat, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 text-center">
                <div className={`w-12 h-12 ${stat.color} rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Award Groups */}
      {awardGroups.map((group, groupIndex) => (
        <section 
          key={groupIndex} 
          className={`py-20 ${groupIndex % 2 === 0 ? 'bg-white' : 'bg-gradient-to-b from-gray-50 to-white'}`}
        >
          <div className="max-w-7xl mx-auto px-6">
            {/* Group Header */}
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-4 mb-6">
                <div className={`w-16 h-1 rounded-full ${groupIndex === 0 ? 'bg-red-600' : 
                               groupIndex === 1 ? 'bg-gray-700' : 
                               'bg-red-600'}`} />
                <div>
                  <div className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                    Ege Bölgesi Sanayi Odası
                  </div>
                  <h2 className="text-4xl font-bold text-gray-900">
                    {group.title}
                  </h2>
                </div>
                <div className={`w-16 h-1 rounded-full ${groupIndex === 0 ? 'bg-red-600' : 
                               groupIndex === 1 ? 'bg-gray-700' : 
                               'bg-red-600'}`} />
              </div>
              
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
                {group.description}
              </p>
              
              <div className="inline-flex items-center gap-2 bg-gray-100 px-6 py-3 rounded-full">
                <span className="text-sm font-medium text-gray-700">Performans Yılı:</span>
                <span className={`text-lg font-bold ${groupIndex === 0 ? 'text-red-600' : 
                               groupIndex === 1 ? 'text-gray-700' : 
                               'text-red-600'}`}>
                  {group.year}
                </span>
              </div>
            </motion.div>

            {/* Award Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {group.cards.map((award, index) => (
                <AwardCard 
                  key={index} 
                  award={award} 
                  index={index} 
                  groupIndex={groupIndex} 
                />
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Legacy Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23dc2626\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]" />        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
              <BadgeCheckIcon className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-semibold text-amber-400 uppercase tracking-wider">Sürekli Başarı</span>
          </div>
          
          <h3 className="text-3xl font-bold text-white mb-8">
            Üst Üste <span className="text-amber-300">3 Yıl</span> Ödül Alan Kompozit Şirketi
          </h3>
          
          <p className="text-xl text-gray-300 leading-relaxed mb-12 max-w-3xl mx-auto">
            2012, 2013 ve 2014 yıllarında üst üste EBSO tarafından ödüllendirilerek, 
            kompozit sektöründe sürdürülebilir başarının öncüsü olduk.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {[
              { value: '100%', label: 'Ödül Devamlılığı', icon: '🏆' },
              { value: '6 Kategori', label: 'Başarı Alanı', icon: '📊' },
              { value: '17 Ödül', label: 'Toplam Başarı', icon: '⭐' }
            ].map((item, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-2xl mb-2">{item.icon}</div>
                <div className="text-3xl font-bold text-white mb-2">{item.value}</div>
                <div className="text-sm text-gray-300">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Awards;